/**
 * Site-wide "Coming Soon" splash gate. When Private Mode is ON (admin toggle),
 * anonymous visitors see only a branded splash with email capture + access-code
 * unlock. Logged-in users bypass. /early-access (+legal pages) stay public.
 */
import React, { useEffect, useState } from "react";
import { api, friendlyAxiosError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Mail, KeyRound, Check, ArrowRight } from "lucide-react";

const NAVY = "#0F1B2D";
const ORANGE = "#FF6A00";
const CREAM = "#F6F1E7";
const CODE_KEY = "resmo_splash_code";
const ALLOWED = ["/early-access", "/privacy", "/terms"];

function Countdown({ target }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!target) return null;
  const diff = new Date(`${target}T00:00:00`).getTime() - now;
  if (isNaN(diff) || diff <= 0) return null;
  const cells = [
    [Math.floor(diff / 86400000), "Days"],
    [Math.floor((diff % 86400000) / 3600000), "Hours"],
    [Math.floor((diff % 3600000) / 60000), "Min"],
    [Math.floor((diff % 60000) / 1000), "Sec"],
  ];
  return (
    <div className="mt-6" data-testid="splash-countdown">
      <div className="font-mono-rm text-[10px] uppercase tracking-[2px] font-bold mb-2" style={{ color: ORANGE }}>
        Launching in
      </div>
      <div className="flex gap-2">
        {cells.map(([v, l]) => (
          <div key={l} className="flex-1 text-center py-2 px-1" style={{ background: NAVY }}>
            <div className="text-2xl font-black tabular-nums" style={{ color: "#FFC145" }}
                 data-testid={`splash-countdown-${l.toLowerCase()}`}>
              {String(v).padStart(2, "0")}
            </div>
            <div className="font-mono-rm text-[9px] uppercase tracking-[2px]" style={{ color: "#94A3B8" }}>{l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Splash({ onUnlock, launchDate }) {
  const [email, setEmail] = useState("");
  const [notifyDone, setNotifyDone] = useState(false);
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [notifyErr, setNotifyErr] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState("");
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [unlockErr, setUnlockErr] = useState("");

  const submitNotify = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setNotifyBusy(true);
    setNotifyErr("");
    try {
      await api.post("/private-mode/notify", { email: email.trim() });
      setNotifyDone(true);
    } catch (err) {
      setNotifyErr(friendlyAxiosError(err));
    } finally {
      setNotifyBusy(false);
    }
  };

  const submitUnlock = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setUnlockBusy(true);
    setUnlockErr("");
    try {
      await api.post("/private-mode/unlock", { code: code.trim() });
      try { localStorage.setItem(CODE_KEY, code.trim()); } catch { /* ignore */ }
      onUnlock();
    } catch (err) {
      setUnlockErr(friendlyAxiosError(err));
    } finally {
      setUnlockBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10"
         style={{
           background: NAVY,
           backgroundImage:
             "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
           backgroundSize: "44px 44px",
         }}
         data-testid="private-splash">
      <div className="w-full max-w-lg" style={{ background: CREAM, border: `3px solid ${NAVY}`, boxShadow: `10px 10px 0 0 ${ORANGE}` }}>
        <div className="px-8 pt-8 pb-6">
          <div className="text-3xl font-black tracking-tight" style={{ color: NAVY, fontFamily: "Arial Black, Arial, sans-serif" }}>
            <span style={{ color: ORANGE }}>R</span>ESMO
          </div>
          <div className="font-mono-rm text-[10px] uppercase tracking-[3px] mt-2 font-bold" style={{ color: ORANGE }}>
            · Private beta — launching soon
          </div>
          <h1 className="text-2xl sm:text-3xl font-black mt-5 leading-tight" style={{ color: NAVY }}>
            Something big is coming.
          </h1>
          <p className="text-sm mt-3 leading-relaxed" style={{ color: "#475569" }}>
            RESMO is your Career Intelligence Companion. Beyond the resume —
            <strong> be seen, be understood, be discovered.</strong> We're putting
            the final touches on it now.
          </p>

          <Countdown target={launchDate} />

          {notifyDone ? (
            <div className="mt-6 flex items-center gap-2 border-2 px-4 py-3"
                 style={{ borderColor: "#0E5A4A", background: "#DCFCE7", color: "#0E5A4A" }}
                 data-testid="splash-notify-success">
              <Check size={16} />
              <span className="text-sm font-bold">You're on the list — we'll email you the moment we launch.</span>
            </div>
          ) : (
            <form onSubmit={submitNotify} className="mt-6" data-testid="splash-notify-form">
              <div className="font-mono-rm text-[10px] uppercase tracking-[2px] font-bold mb-2" style={{ color: NAVY }}>
                Get notified at launch
              </div>
              <div className="flex gap-2">
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                       placeholder="you@email.com"
                       className="flex-1 px-3 py-2.5 text-sm bg-white outline-none"
                       style={{ border: `2px solid ${NAVY}`, color: NAVY }}
                       data-testid="splash-email-input" />
                <button type="submit" disabled={notifyBusy}
                        className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 disabled:opacity-60"
                        style={{ background: ORANGE, color: "#fff", border: `2px solid ${NAVY}`, boxShadow: `3px 3px 0 0 ${NAVY}` }}
                        data-testid="splash-notify-btn">
                  <Mail size={13} /> {notifyBusy ? "Saving…" : "Notify me"}
                </button>
              </div>
              {notifyErr && <div className="text-xs font-bold mt-2" style={{ color: "#B91C1C" }} data-testid="splash-notify-error">{notifyErr}</div>}
            </form>
          )}

          <a href="/early-access"
             className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold underline underline-offset-4"
             style={{ color: NAVY }} data-testid="splash-story-link">
            Want in sooner? Share your story <ArrowRight size={14} />
          </a>
        </div>

        <div className="px-8 py-5" style={{ borderTop: `2px dashed #CBD5E1` }}>
          {!showCode ? (
            <button onClick={() => setShowCode(true)}
                    className="font-mono-rm text-[11px] uppercase tracking-[2px] font-bold inline-flex items-center gap-1.5"
                    style={{ color: "#64748B" }} data-testid="splash-code-toggle">
              <KeyRound size={12} /> Have an access code?
            </button>
          ) : (
            <form onSubmit={submitUnlock} data-testid="splash-code-form">
              <div className="font-mono-rm text-[10px] uppercase tracking-[2px] font-bold mb-2" style={{ color: NAVY }}>
                Enter your access code
              </div>
              <div className="flex gap-2">
                <input type="text" value={code} onChange={(e) => setCode(e.target.value)}
                       placeholder="ACCESS CODE" autoFocus
                       className="flex-1 px-3 py-2.5 text-sm bg-white outline-none font-mono-rm uppercase tracking-widest"
                       style={{ border: `2px solid ${NAVY}`, color: NAVY }}
                       data-testid="splash-code-input" />
                <button type="submit" disabled={unlockBusy}
                        className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider disabled:opacity-60"
                        style={{ background: NAVY, color: "#FFC145", border: `2px solid ${NAVY}`, boxShadow: `3px 3px 0 0 ${ORANGE}` }}
                        data-testid="splash-unlock-btn">
                  {unlockBusy ? "Checking…" : "Unlock"}
                </button>
              </div>
              {unlockErr && <div className="text-xs font-bold mt-2" style={{ color: "#B91C1C" }} data-testid="splash-unlock-error">{unlockErr}</div>}
            </form>
          )}
          <div className="font-mono-rm text-[9px] uppercase tracking-[2px] mt-4" style={{ color: "#94A3B8" }}>
            Every career begins with a SPARK.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PrivateModeGate({ children }) {
  const { user } = useAuth(); // null=loading, false=logged out, object=logged in
  const [status, setStatus] = useState(null);
  const path = typeof window !== "undefined" ? window.location.pathname : "";

  useEffect(() => {
    let stored = "";
    try { stored = localStorage.getItem(CODE_KEY) || ""; } catch { /* ignore */ }
    api.get("/private-mode/status", { params: stored ? { code: stored } : {} })
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ enabled: true, unlocked: false })); // fail closed — privacy first
  }, []);

  if (ALLOWED.includes(path)) return children;
  if (status === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: NAVY, color: "#FFC145" }}>
        <div className="font-mono-rm text-xs uppercase tracking-widest">Loading…</div>
      </div>
    );
  }
  if (!status.enabled || status.unlocked) return children;
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: NAVY, color: "#FFC145" }}>
        <div className="font-mono-rm text-xs uppercase tracking-widest">Loading…</div>
      </div>
    );
  }
  if (user) return children;
  return <Splash launchDate={status.launch_date} onUnlock={() => setStatus((s) => ({ ...s, unlocked: true }))} />;
}
